import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { TrendingUp, TrendingDown, Minus, Clock, CheckCircle, AlertTriangle, Activity, Info } from "lucide-react";
import { DashboardStats, ThroughputMetrics } from "../types/statistics.types";

interface StatisticsKpiCardsProps {
  dashboard: DashboardStats | null;
  throughput: ThroughputMetrics | null;
  loading: boolean;
}

export function StatisticsKpiCards({ dashboard, throughput, loading }: StatisticsKpiCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="bg-white">
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Determine doorlooptijd color and trend
  const avgDays = throughput?.avgDays ?? 0;
  const getDoorlooptijdColor = (days: number) => {
    if (days <= 7) return 'text-green-600';
    if (days <= 14) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDoorlooptijdBg = (days: number) => {
    if (days <= 7) return 'bg-green-50';
    if (days <= 14) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  // SLA compliance color
  const slaCompliance = throughput?.slaComplianceRate ?? 100;
  const getSlaColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSlaBg = (rate: number) => {
    if (rate >= 90) return 'bg-green-50';
    if (rate >= 75) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  // Backlog health color
  const backlogHealth = dashboard?.backlogHealthScore ?? 0;
  const getHealthColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score: number) => {
    if (score >= 70) return 'bg-green-50';
    if (score >= 50) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  // Volume trend
  const netChange = (dashboard?.newThisWeek ?? 0) - (dashboard?.completedThisWeek ?? 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Doorlooptijd */}
      <Card className={`bg-white ${getDoorlooptijdBg(avgDays)}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gem. Doorlooptijd
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs bg-neutral-800 text-white p-3">
                  <p className="font-medium mb-1">Hoe wordt dit berekend?</p>
                  <p className="text-xs">
                    Gemiddeld aantal dagen van ticket creatie (createdAt) tot voltooiing
                    (completedAt/fase "Voltooid"). Alleen tickets afgerond na 1 nov 2025
                    worden meegenomen.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Clock className={`h-4 w-4 ${getDoorlooptijdColor(avgDays)}`} />
        </CardHeader>
        <CardContent>
          <div className={`text-3xl font-bold ${getDoorlooptijdColor(avgDays)}`}>
            {avgDays.toFixed(1)} dagen
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Mediaan: {throughput?.medianDays?.toFixed(1) ?? 0} dagen
          </p>
        </CardContent>
      </Card>

      {/* SLA Compliance */}
      <Card className={`bg-white ${getSlaBg(slaCompliance)}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              SLA Compliance
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs bg-neutral-800 text-white p-3">
                  <p className="font-medium mb-1">Doorlooptijd SLA targets:</p>
                  <ul className="text-xs space-y-0.5">
                    <li>• Hoog: max 5 dagen</li>
                    <li>• Gemiddeld: max 10 dagen</li>
                    <li>• Laag: max 20 dagen</li>
                  </ul>
                  <p className="text-xs mt-2">
                    Percentage tickets afgerond binnen de SLA target voor hun prioriteit.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <CheckCircle className={`h-4 w-4 ${getSlaColor(slaCompliance)}`} />
        </CardHeader>
        <CardContent>
          <div className={`text-3xl font-bold ${getSlaColor(slaCompliance)}`}>
            {slaCompliance}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {throughput?.withinSla ?? 0} van {throughput?.totalCompleted ?? 0} binnen SLA
          </p>
        </CardContent>
      </Card>

      {/* Open Tickets */}
      <Card className="bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Open Tickets
          </CardTitle>
          <AlertTriangle className="h-4 w-4 text-neutral-500" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-neutral-800">
            {dashboard?.openTickets ?? 0}
          </div>
          <div className="flex items-center gap-1 text-xs mt-1">
            {netChange > 0 ? (
              <>
                <TrendingUp className="h-3 w-3 text-red-500" />
                <span className="text-red-500">+{netChange} deze week</span>
              </>
            ) : netChange < 0 ? (
              <>
                <TrendingDown className="h-3 w-3 text-green-500" />
                <span className="text-green-500">{netChange} deze week</span>
              </>
            ) : (
              <>
                <Minus className="h-3 w-3 text-neutral-500" />
                <span className="text-neutral-500">Stabiel deze week</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Backlog Health */}
      <Card className={`bg-white ${getHealthBg(backlogHealth)}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Backlog Gezondheid
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm bg-neutral-800 text-white p-3">
                  <p className="text-xs mb-2">
                    Hoe "vers" is je backlog? Elk open ticket krijgt strafpunten op basis van leeftijd. Hoe ouder, hoe meer strafpunten.
                  </p>
                  <p className="font-medium text-xs mb-1">Strafpunten:</p>
                  <p className="text-xs mb-2">
                    0-3d: 0 | 3-7d: 5 | 7-14d: 15 | 14-30d: 25 | 30+d: 50
                  </p>
                  <p className="font-medium text-xs mb-1">Voorbeeld met 5 tickets:</p>
                  <ul className="text-xs space-y-0.5 mb-2">
                    <li>• Ticket A: 2 dagen oud → 0 punten</li>
                    <li>• Ticket B: 5 dagen oud → 5 punten</li>
                    <li>• Ticket C: 10 dagen oud → 15 punten</li>
                    <li>• Ticket D: 20 dagen oud → 25 punten</li>
                    <li>• Ticket E: 45 dagen oud → 50 punten</li>
                  </ul>
                  <p className="text-xs">
                    Totaal: 95 punten ÷ 5 tickets = 19 gemiddeld<br/>
                    <strong>Score: 100 - 19 = 81</strong>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Activity className={`h-4 w-4 ${getHealthColor(backlogHealth)}`} />
        </CardHeader>
        <CardContent>
          <div className={`text-3xl font-bold ${getHealthColor(backlogHealth)}`}>
            {backlogHealth}/100
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Gem. leeftijd: {dashboard?.avgOpenDays ?? 0} dagen
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
